import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import React, { useEffect, useMemo } from 'react';
import { useNotificationSetup } from './useNotificationSetup';
import Echo from 'laravel-echo';
import { addNotification } from 'src/store/notifications/slice';
import { NotificationIcon } from 'src/components/common/icons/NotificationIcon/NotificationIcon';
import { ClickAwayListener, Popper } from '@material-ui/core';
import { NotificationsSection } from './NotificationsSection';
import { INotification } from 'src/api/base/other';
// @ts-ignore
import socketIO from 'socket.io-client';

interface INotificationEvent {
  notification: INotification;
}

const useAppNotifications = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const { notifications } = useSelector(
    (state: RootState) => state.notifications,
  );

  const newNotificationsCount = useMemo(() => {
    return notifications.filter((notification) => !notification.read_at).length;
  }, [notifications]);

  const { isLoading } = useNotificationSetup();

  const socket = useMemo(
    () =>
      new Echo({
        host: `${process.env.REACT_APP_API_URL}`,
        transports: ['websocket'],
        broadcaster: 'socket.io',
        client: socketIO,
      }),
    [],
  );

  useEffect(() => {
    if (socket && socket?.channel) {
      socket
        .channel(`laravel_database_newNotification-${user?.id}`)
        .listen('.NotificationEvent', (e: INotificationEvent) => {
          dispatch(addNotification(e.notification));
        });
    }

    return () => {
      socket.leaveChannel(`laravel_database_newNotification-${user?.id}`);
    };
  }, [dispatch, socket, user]);

  return {
    isLoading,
    newNotificationsCount,
    notifications,
  };
};

export const AppNotifications: React.FC = () => {
  const { isLoading, newNotificationsCount, notifications } =
    useAppNotifications();

  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(
      notificationsAnchorEl ? null : event.currentTarget,
    );
  };
  const openedNotificationsAnchorEl = Boolean(notificationsAnchorEl);
  const notificationsPopperId = openedNotificationsAnchorEl
    ? 'notifications'
    : undefined;

  const handleClickAway = (
    event: React.MouseEvent<Document, globalThis.MouseEvent>,
  ) => {
    if (event.target === notificationsAnchorEl) return;
    setNotificationsAnchorEl(null);
  };

  return (
    <>
      <NotificationIcon
        style={{ cursor: 'pointer' }}
        onClick={handleNotificationsClick}
        notificationsCount={newNotificationsCount}
      />
      <Popper
        id={notificationsPopperId}
        open={openedNotificationsAnchorEl}
        anchorEl={notificationsAnchorEl}
        placement="bottom-start"
        style={{ zIndex: 100 }}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <NotificationsSection
              loading={isLoading}
              notifications={notifications}
            />
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
